import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Login successful!');
      
      if (user.role === 'student') {
        navigate('/student');
      } else if (user.role === 'super_admin') {
        navigate('/admin/super');
      } else if (user.role === 'tenant_admin') {
        navigate('/admin/tenant');
      } else if (user.role === 'content_admin') {
        navigate('/admin/content');
      } else if (user.role === 'institution_admin') {
        navigate('/admin/institution');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_e147f8d8-c7cf-461e-90dc-648b43a5b2be/artifacts/d0j1yj5f_AR%20Smart%20Books%20Logo%20Transparent.png" 
              alt="AR Smart Books" 
              className="h-24 w-auto"
            />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-slate-800">
            Transforming Textbooks into 3D
          </h1>
          <p className="text-slate-600 flex items-center justify-center gap-2 text-sm sm:text-base">
            <Sparkles className="w-4 h-4 text-cyan-500" />
            Interactive AR Learning Experience
          </p>
        </div>

        <Card data-testid="login-card" className="shadow-xl border-slate-200">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-indigo-900">Welcome Back</CardTitle>
            <CardDescription>Sign in to continue your learning journey</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Email
                </label>
                <Input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="h-11 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Password
                </label>
                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full rounded-full h-11 bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/30"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-xs font-semibold text-indigo-900 mb-2">Demo Credentials:</p>
              <div className="space-y-1 text-xs text-indigo-700">
                <p><strong>Student:</strong> student@demo.com / student123</p>
                <p><strong>Super Admin:</strong> superadmin@artextbooks.com / admin123</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-4 sm:mt-6 text-xs sm:text-sm text-slate-600">
          <p>Developed by <span className="font-medium text-slate-800">Any Tech Ventures Pvt. Ltd.</span></p>
        </div>
      </motion.div>
    </div>
  );
};